import React, { useEffect, useMemo, useRef } from "react";
import {
  BulkDeleteButton,
  Create,
  Datagrid,
  Edit,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { CrudPermissions } from "../../auth/crud-permissions";
import TuneIcon from "@mui/icons-material/Tune";
import {
  AdSources,
  TrackAccountId,
  TrackCampaignId,
  TrackChannel,
  TrackingQueryParamKeysBySource,
} from "@arrowsup/platform-dtos";
import { TopHelpInfoText } from "../../help/top-help-info-text";

export const CustomTrackingParamResource = (perms: CrudPermissions) => {
  return (
    <Resource
      name="custom-tracking-param"
      key={"custom-tracking-param"}
      options={{
        label: "Custom Tracking Params",
      }}
      list={CustomTrackingParamList(perms)}
      edit={perms.update ? CustomTrackingParamEdit : undefined}
      create={perms.create ? CustomTrackingParamCreate : undefined}
      icon={TuneIcon}
    />
  );
};

export const CustomTrackingParamList = (perms: CrudPermissions) => {
  return (
    <>
      <TopHelpInfoText text="Define custom query parameters to track from page loads and use for conversion attribution." />
      <List>
        <Datagrid
          rowClick={perms.update ? "edit" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="ID" />
          <TextField source="param" label="Param" />
          <TextField source="adSource" label="Channel" />
          <TextField source="represents" label="Represents" />
        </Datagrid>
      </List>
    </>
  );
};

const AdSourceInput: React.FC = () => {
  return (
    <SelectInput
      source="adSource"
      label="Channel"
      helperText="Which channel to associate with this custom param?"
      choices={AdSources.map((v) => ({
        id: v,
        name: v,
      }))}
      fullWidth
      required
      sx={{ pb: 2 }}
    />
  );
};

const ParamInput: React.FC = () => {
  return (
    <TextInput
      source="param"
      label="Param"
      helperText="The query parameter name to track (e.g., 'utm_campaign')"
      fullWidth
      required
      sx={{ pb: 2 }}
    />
  );
};

const RepresentsInput: React.FC = () => {
  const adSource = useWatch<{ adSource: AdSources }>({ name: "adSource" });
  const { resetField } = useFormContext();
  const lastAdSource = useRef(adSource);

  // Choices are channel-specific, so a selection made for the previous channel
  // would linger in form state - invisible in the select but still submitted.
  useEffect(() => {
    if (
      lastAdSource.current !== undefined &&
      lastAdSource.current !== adSource
    ) {
      resetField("represents", { defaultValue: "" });
    }

    lastAdSource.current = adSource;
  }, [adSource, resetField]);

  // Valid params for the selected channel, plus the concepts always allowed.
  const choices = useMemo(() => {
    const sourceParams = adSource
      ? TrackingQueryParamKeysBySource[adSource]
      : [];

    const paramChoices = sourceParams.map((p) => ({ id: p, name: p }));

    return [
      ...paramChoices,
      { id: TrackAccountId, name: TrackAccountId },
      { id: TrackCampaignId, name: TrackCampaignId },
      { id: TrackChannel, name: TrackChannel },
    ];
  }, [adSource]);

  return (
    <SelectInput
      source="represents"
      label="Represents"
      helperText="What this custom param represents - a known tracking key, Account ID, Campaign ID, or Channel (pick Channel when the param's presence alone means the visit came from this channel, so only use params you append exclusively on its links)"
      choices={choices}
      fullWidth
      required
      sx={{ pb: 2 }}
    />
  );
};

export const CustomTrackingParamEdit: React.FC = () => {
  return (
    <Edit redirect="list">
      <SimpleForm>
        <ParamInput />
        <AdSourceInput />
        <RepresentsInput />
      </SimpleForm>
    </Edit>
  );
};

export const CustomTrackingParamCreate: React.FC = () => {
  return (
    <Create redirect="list">
      <SimpleForm>
        <ParamInput />
        <AdSourceInput />
        <RepresentsInput />
      </SimpleForm>
    </Create>
  );
};
